/**
 * Building blocks shared by the built-in templates: house styles, the ISO
 * 7200-style title block, revision table, compact title strip.
 *
 * Every helper takes an id prefix — element ids are unique across a whole
 * template, so blocks reused on several pages need distinct prefixes.
 */

import { PRINT_BLACK, PRINT_WHITE, type PrintColor } from '../color';
import {
  PRINT_TEMPLATE_SCHEMA_VERSION,
  type GroupElement,
  type NamedTextStyle,
  type NorthArrowElement,
  type PrintFieldDef,
  type PrintTemplateDefaults,
  type PrintTemplateElement,
  type RectElement,
  type ScaleBarElement,
  type StrokeStyle,
  type TableElement,
  type TextElement,
  type TextStyleRef,
} from '../spec';

export { PRINT_TEMPLATE_SCHEMA_VERSION };

export const LABEL_GRAY: PrintColor = { space: 'gray', k: 0.6 };

export function pen(weightMm: number, color: PrintColor = PRINT_BLACK): StrokeStyle {
  return { color, weightMm, dashMm: [], cap: 'butt', join: 'miter' };
}

export const THIN = pen(0.18);
export const MEDIUM = pen(0.35);
export const HEAVY = pen(0.7);

export const BUILTIN_DEFAULTS: PrintTemplateDefaults = {
  textStyle: {
    font: { source: 'bundled', family: 'Inter' },
    size: { value: 9, unit: 'pt', basis: 'em' },
    weight: 'normal',
    italic: false,
    color: PRINT_BLACK,
    lineHeight: 1.2,
    letterSpacingMm: 0,
  },
  stroke: THIN,
};

export const BUILTIN_STYLES: NamedTextStyle[] = [
  { id: 'label', name: 'Cell label', style: { size: { value: 5.5, unit: 'pt', basis: 'em' }, color: LABEL_GRAY, letterSpacingMm: 0.1 } },
  { id: 'value', name: 'Cell value', style: { size: { value: 9, unit: 'pt', basis: 'em' } } },
  { id: 'valueStrong', name: 'Cell value (strong)', style: { size: { value: 11, unit: 'pt', basis: 'em' }, weight: 'bold' } },
  { id: 'title', name: 'Title', style: { size: { value: 16, unit: 'pt', basis: 'em' }, weight: 'bold' } },
  { id: 'small', name: 'Small print', style: { size: { value: 7, unit: 'pt', basis: 'em' } } },
];

export const BUILTIN_FIELDS: PrintFieldDef[] = [
  { key: 'client', label: 'Client', type: 'text', options: [], defaultValue: '{project.client}' },
  { key: 'drawnBy', label: 'Drawn by', type: 'text', options: [], defaultValue: '{user.name}' },
  { key: 'checkedBy', label: 'Checked by', type: 'text', options: [], defaultValue: '' },
  { key: 'drawingNumber', label: 'Drawing number', type: 'text', options: [], defaultValue: '{sheet.number}' },
  { key: 'status', label: 'Status', type: 'select', options: ['Draft', 'For approval', 'As built'], defaultValue: 'As built' },
];

const style = (styleId: string): TextStyleRef => ({ styleId });

export function text(id: string, xMm: number, yMm: number, wMm: number, hMm: number, content: string, styleRef: TextStyleRef, extra: Partial<TextElement> = {}): TextElement {
  return { id, kind: 'text', xMm, yMm, wMm, hMm, rotationDeg: 0, content, style: styleRef, align: 'left', valign: 'top', autoShrink: true, paddingMm: 0, ...extra };
}

export function rect(id: string, xMm: number, yMm: number, wMm: number, hMm: number, stroke: StrokeStyle | null, fill: PrintColor | null = null): RectElement {
  return { id, kind: 'rect', xMm, yMm, wMm, hMm, rotationDeg: 0, stroke, fill, cornerRadiusMm: 0 };
}

/** A bordered title-block cell: small gray caption on top, value below. */
function cell(prefix: string, xMm: number, yMm: number, wMm: number, hMm: number, caption: string, value: string, valueStyle = 'value'): PrintTemplateElement[] {
  return [
    rect(`${prefix}_box`, xMm, yMm, wMm, hMm, THIN),
    text(`${prefix}_cap`, xMm + 1.5, yMm + 1.2, wMm - 3, 2.5, caption.toUpperCase(), style('label')),
    text(`${prefix}_val`, xMm + 1.5, yMm + 4, wMm - 3, hMm - 5, value, style(valueStyle), { valign: 'middle' }),
  ];
}

export const TITLE_BLOCK_SIZE = { wMm: 180, hMm: 56 } as const;

/** ISO 7200-style title block, 180 × 56 mm, placed with its top-left at (xMm, yMm). */
export function titleBlock(prefix: string, xMm: number, yMm: number): GroupElement {
  const { wMm, hMm } = TITLE_BLOCK_SIZE;
  const children: PrintTemplateElement[] = [
    rect(`${prefix}_bg`, 0, 0, wMm, hMm, MEDIUM, PRINT_WHITE),
    { id: `${prefix}_logo`, kind: 'image', xMm: 3, yMm: 3, wMm: 54, hMm: 24, rotationDeg: 0, source: { kind: 'orgLogo' }, fit: 'contain', align: 'left' },
    text(`${prefix}_org`, 3, 30, 54, 23, '{org.name}\n{org.address}\n{org.phone}\n{org.email}', style('small')),
    ...cell(`${prefix}_project`, 60, 0, 120, 14, 'Project', '{project.name}', 'valueStrong'),
    ...cell(`${prefix}_title`, 60, 14, 120, 14, 'Drawing', '{sheet.name} — {floor.name}', 'valueStrong'),
    ...cell(`${prefix}_client`, 60, 28, 50, 14, 'Client', '{field.client}'),
    ...cell(`${prefix}_drawn`, 110, 28, 35, 14, 'Drawn', '{field.drawnBy}'),
    ...cell(`${prefix}_checked`, 145, 28, 35, 14, 'Checked', '{field.checkedBy}'),
    ...cell(`${prefix}_scale`, 60, 42, 22, 14, 'Scale', '{scale}'),
    ...cell(`${prefix}_date`, 82, 42, 26, 14, 'Date', '{date}'),
    ...cell(`${prefix}_number`, 108, 42, 32, 14, 'Drawing no.', '{field.drawingNumber}'),
    ...cell(`${prefix}_rev`, 140, 42, 14, 14, 'Rev', '{sheet.rev}'),
    ...cell(`${prefix}_page`, 154, 42, 26, 14, 'Sheet', '{page.number} / {page.count}'),
  ];
  return { id: prefix, name: 'Title block', kind: 'group', xMm, yMm, wMm, hMm, rotationDeg: 0, children };
}

/** Revision block, 180 mm wide, growing upwards from its bottom edge. */
export function revisionTable(id: string, xMm: number, bottomMm: number, maxRows = 4): TableElement {
  const rowHeightMm = 5;
  const hMm = rowHeightMm * (maxRows + 1);
  return {
    id,
    name: 'Revisions',
    kind: 'table',
    xMm,
    yMm: bottomMm - hMm,
    wMm: TITLE_BLOCK_SIZE.wMm,
    hMm,
    rotationDeg: 0,
    columns: [
      { id: 'rev', header: 'Rev', widthMm: 14, cell: '{rev.rev}', align: 'center' },
      { id: 'date', header: 'Date', widthMm: 26, cell: '{rev.date}', align: 'left' },
      { id: 'description', header: 'Description', widthMm: 105, cell: '{rev.description}', align: 'left' },
      { id: 'by', header: 'By', widthMm: 35, cell: '{rev.by}', align: 'left' },
    ],
    rows: { kind: 'revisions', maxRows, newestFirst: true },
    showHeader: true,
    rowHeightMm,
    growUp: true,
    headerStyle: { styleId: 'label' },
    cellStyle: { styleId: 'small' },
    stroke: THIN,
  };
}

export function scaleBar(id: string, viewportId: string, xMm: number, yMm: number, wMm = 70): ScaleBarElement {
  return { id, kind: 'scaleBar', xMm, yMm, wMm, hMm: 8, rotationDeg: 0, viewportId, segments: 4, stroke: THIN, fill: PRINT_BLACK, labelStyle: { styleId: 'small' } };
}

export function northArrow(id: string, viewportId: string, xMm: number, yMm: number, sizeMm = 16): NorthArrowElement {
  return { id, kind: 'northArrow', xMm, yMm, wMm: sizeMm, hMm: sizeMm * 1.25, rotationDeg: 0, viewportId, variant: 'arrow', stroke: THIN, fill: PRINT_BLACK, labelStyle: { styleId: 'value' } };
}

/** One-line title strip for overview sheets: logo, project + drawing, scale, date, page. */
export function titleStrip(prefix: string, xMm: number, yMm: number, wMm: number): GroupElement {
  const hMm = 16;
  return {
    id: prefix,
    name: 'Title strip',
    kind: 'group',
    xMm,
    yMm,
    wMm,
    hMm,
    rotationDeg: 0,
    children: [
      rect(`${prefix}_bg`, 0, 0, wMm, hMm, MEDIUM, PRINT_WHITE),
      { id: `${prefix}_logo`, kind: 'image', xMm: 2, yMm: 2, wMm: 34, hMm: 12, rotationDeg: 0, source: { kind: 'orgLogo' }, fit: 'contain', align: 'left' },
      text(`${prefix}_project`, 40, 2, wMm - 120, 6, '{project.name}', style('valueStrong')),
      text(`${prefix}_title`, 40, 9, wMm - 120, 5, '{sheet.name} — {floor.name}', style('value')),
      ...cell(`${prefix}_scale`, wMm - 78, 0, 22, hMm, 'Scale', '{scale}'),
      ...cell(`${prefix}_date`, wMm - 56, 0, 30, hMm, 'Date', '{date}'),
      ...cell(`${prefix}_page`, wMm - 26, 0, 26, hMm, 'Sheet', '{page.number} / {page.count}'),
    ],
  };
}
